import { ScheduledActivityResponse } from '../types/scheduledActivity';

export interface TimePeriodSectionConfig {
  key: string;
  label: string;
  hours: string | null;
  headerColor: string;
  transitionName: string | null; // matches ActivityTransition.name; null only as fallback
}

export const TIME_PERIOD_SECTIONS: TimePeriodSectionConfig[] = [
  {
    key: 'whole_day',
    label: 'Whole Day',
    hours: null,
    headerColor: '#6B7280',
    transitionName: 'whole_day',
  },
  {
    key: 'morning',
    label: 'Morning',
    hours: '6am–12pm',
    headerColor: '#005880',
    transitionName: 'morning',
  },
  {
    key: 'after_school',
    label: 'After school/nursery',
    hours: '12pm–4pm',
    headerColor: '#0080D0',
    transitionName: 'after_school',
  },
  {
    key: 'evening',
    label: 'Evening, after dinner',
    hours: '4pm–8pm',
    headerColor: '#3537D6',
    transitionName: 'evening',
  },
  {
    key: 'bedtime',
    label: 'Bedtime',
    hours: '8pm–6am',
    headerColor: '#4646FF',
    transitionName: 'bedtime',
  },
];

/**
 * Resolve TIME_PERIOD_SECTIONS against fetched transitions to get section-to-transitionId mapping.
 * Returns a map: sectionKey → transitionId (string) or null (for Whole Day).
 */
export function resolveTransitionIds(
  transitions: { id: string; name: string; label: string }[]
): Map<string, string | null> {
  const nameToId = new Map(transitions.map(t => [t.name, t.id]));
  const result = new Map<string, string | null>();

  for (const section of TIME_PERIOD_SECTIONS) {
    if (section.transitionName === null) {
      result.set(section.key, null);
    } else {
      result.set(section.key, nameToId.get(section.transitionName) ?? null);
    }
  }

  return result;
}

/**
 * Group scheduled activities by their time period section.
 * Returns a map: sectionKey → ScheduledActivityResponse[], sorted by position within each group.
 */
export function groupByTimePeriod(
  scheduledActivities: ScheduledActivityResponse[],
  sectionToTransitionId: Map<string, string | null>
): Map<string, ScheduledActivityResponse[]> {
  // Build reverse map: transitionId → sectionKey
  const transitionIdToSection = new Map<string | null, string>();
  for (const [sectionKey, transitionId] of sectionToTransitionId) {
    transitionIdToSection.set(transitionId, sectionKey);
  }

  // Initialize empty groups for all sections
  const groups = new Map<string, ScheduledActivityResponse[]>();
  for (const section of TIME_PERIOD_SECTIONS) {
    groups.set(section.key, []);
  }

  // Group activities
  for (const sa of scheduledActivities) {
    const sectionKey = transitionIdToSection.get(sa.activityTransitionId) ?? 'whole_day';
    const group = groups.get(sectionKey);
    if (group) {
      group.push(sa);
    }
  }

  // Sort each group by position
  for (const [, items] of groups) {
    items.sort((a, b) => a.position - b.position);
  }

  return groups;
}

/**
 * Determine which section an activity should be placed in when added.
 * Returns the transitionId (string) for the first associated time period, or null for Whole Day.
 *
 * @param activityTransitions - The activity's transition associations (from AdminActivity.transitions)
 * @param orderedTransitions - All transitions ordered by startHour (from useTransitions)
 */
export function getTargetTransitionId(
  activityTransitions: { id: string; name: string }[],
  orderedTransitions: { id: string; name: string }[]
): string | null {
  if (activityTransitions.length === 0) return null;

  const activityTransitionIds = new Set(activityTransitions.map(t => t.id));
  for (const t of orderedTransitions) {
    if (activityTransitionIds.has(t.id)) {
      return t.id;
    }
  }

  return null;
}
